// 9fbef606107a605d69c0edbcd8029e5d
// Temp fix for DAM image in PLP
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ReactInputPosition, { MOUSE_ACTIVATION } from 'react-input-position';
import { getVideoUrl, isSafariBrowser, getDynamicBadgePackQty } from '@tcp/core/src/utils/utils';
import RenderPerf from '@tcp/web/src/components/common/molecules/RenderPerf';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import { HERO_VISIBLE } from '@tcp/core/src/constants/rum.constants';
import useBooleanState from '@tcp/core/src/hooks/useBooleanState';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import DamImage from '@tcp/core/src/components/common/atoms/DamImage';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import ImageRenderer from '../ImageRenderer/views/ImageRenderer.view';
import withStyles from '../../hoc/withStyles';
import styles, { PseudoPdpImg } from './ProductDetailImage.style';
import { getLocator, canUseDOM, getViewportInfo } from '../../../../utils';
import config from './config';
import { VID_DATA_SLP } from '../../../features/browse/ProductListing/molecules/ProductList/config';
import SafeComponent from '../../hoc/withErrorBoundary/errorBoundary';

const getVideoTransformation = ({ VID_DATA_CLIENT, VID_DATA_CLIENT_WEBP }) => {
  const { isMobile } = getViewportInfo() || {};
  if (isMobile) {
    return VID_DATA_SLP;
  }
  return isSafariBrowser() ? VID_DATA_CLIENT : VID_DATA_CLIENT_WEBP;
};

const getImgConfig = (fromPLPPage, isDamImageV2Enabled, isDamImageV2EnabledPDP) => {
  if (fromPLPPage) {
    return isDamImageV2Enabled || isDamImageV2EnabledPDP
      ? config.IMG_DATA_CLIENT_V2
      : config.IMG_DATA_CLIENT;
  }
  return isDamImageV2Enabled || isDamImageV2EnabledPDP ? config.IMG_DATA_V2 : config.IMG_DATA;
};

const getMainConfig = (isVideoUrl, imgConfig) => {
  const { isMobile } = getViewportInfo() || {};
  if (isMobile && isVideoUrl) return VID_DATA_SLP;
  return isVideoUrl ? config.VID_DATA : imgConfig;
};

const imgConfigsFunc = (fromSkeleton, skeletonConfig, mainConfig) => {
  return fromSkeleton ? skeletonConfig.imgConfig : mainConfig.imgConfig;
};

let imageMagnifierEnabler = false;
let productSectionWidth = 0;
let mainImageHeight = 0;
let imageMagnifierEnablerBundle = false;
let productSectionWidthBundle = 0;
let mainImageHeightBundle = 0;

const getBundleZoomCheck = ({ isFromBundlePage, isCollectionsZoomEnabled }) => {
  return isFromBundlePage && !isCollectionsZoomEnabled;
};

const getBundleZoomClass = (isCollectionsZoomEnabled) => {
  return isCollectionsZoomEnabled ? ' collection-zoom-enabled' : '';
};

const getVideoUrlClass = (isVideoUrl) => {
  return isVideoUrl ? ' bundle-video-desktop' : '';
};

const NonZoomImage = (imgProps) => {
  const {
    className,
    imageUrl,
    imageName,
    onOpenSimpleFullSize,
    fromSkeleton,
    dynamicBadgeOverlayQty,
    badgeConfig,
    tcpStyleType,
    fromPLPPage,
    isNewQVEnabled,
    primaryBrand,
    alternateBrand,
    isStyleWith,
    preloadImage = false,
    renderDiscoveryButton,
    onLoadImage,
    isDamImageV2Enabled,
    isDamImageV2EnabledPDP,
    isFromBundlePage,
    fromQv,
    isCollectionsZoomEnabled,
    imageConfigWithoutDPR,
    fromBagPage,
    isFromHeroProductTile,
    isDynamicBadgeEnabled,
    isDynamicBadgeEnabledAbtest,
  } = imgProps;
  const [isImageLoaded, handleImageLoaded] = useBooleanState(false);
  const imgData = {
    alt: imageName,
    url: imageUrl,
  };
  const imageWrapperHeight = {
    minHeight: isNewQVEnabled ? '200px' : '380px',
  };

  if (isStyleWith) {
    imageWrapperHeight.minHeight = '100px';
  }
  const imgConfig = getImgConfig(fromPLPPage, isDamImageV2Enabled, isDamImageV2EnabledPDP);
  const isVideoUrl = getVideoUrl(imgData.url);
  const skeletonConfig = isVideoUrl ? getVideoTransformation(config) : imgConfig;
  const mainConfig = getMainConfig(isVideoUrl, imgConfig);

  const bundleZoomClass = getBundleZoomClass(isCollectionsZoomEnabled);
  const videoUrlClass = getVideoUrlClass(isVideoUrl);
  return (
    <>
      {' '}
      <div className={`show-non-zoom-image${bundleZoomClass}${videoUrlClass}`}>
        <DamImage
          className={`${className} full-size-desktop-image`}
          data-locator={getLocator('pdp_main_image')}
          imgData={imgData}
          itemProp="contentUrl"
          isProductImage
          lazyLoad={false}
          imgConfigs={fromSkeleton ? skeletonConfig.imgConfig : mainConfig.imgConfig}
          onLoad={onLoadImage || handleImageLoaded}
          isOptimizedVideo={fromSkeleton}
          dynamicBadgeOverlayQty={dynamicBadgeOverlayQty}
          badgeConfig={badgeConfig}
          tcpStyleType={tcpStyleType}
          primaryBrand={primaryBrand || alternateBrand}
          renderDiscoveryButton={renderDiscoveryButton}
          isDamImageV2EnabledPDP={isDamImageV2EnabledPDP}
          isDamImageV2Enabled={isDamImageV2Enabled}
          imageConfigWithoutDPR={imageConfigWithoutDPR}
          fromBagPage={fromBagPage}
          isFromHeroProductTile={isFromHeroProductTile}
          isDynamicBadgeEnabled={isDynamicBadgeEnabled}
          isDynamicBadgeEnabledAbtest={isDynamicBadgeEnabledAbtest}
        />
      </div>
      <div
        className={`${
          getBundleZoomCheck({ isFromBundlePage, isCollectionsZoomEnabled })
            ? 'show-on-mobile'
            : 'hide-on-desktop '
        } ${fromQv ? 'hide-on-tablet' : ''}`}
      >
        <BodyCopy style={imageWrapperHeight}>
          {getBundleZoomCheck({ isFromBundlePage, isCollectionsZoomEnabled }) ? (
            <DamImage
              className="full-size-desktop-image"
              data-locator={getLocator('pdp_main_image')}
              imgData={imgData}
              itemProp="contentUrl"
              isProductImage
              lazyLoad={false}
              imgConfigs={imgConfigsFunc(fromSkeleton, skeletonConfig, mainConfig)} // TODO check for imageConfig in case of having cloudinary name form for bundle
              onLoad={onLoadImage || handleImageLoaded}
              isOptimizedVideo
              dynamicBadgeOverlayQty={dynamicBadgeOverlayQty}
              badgeConfig={badgeConfig}
              tcpStyleType={tcpStyleType}
              primaryBrand={primaryBrand || alternateBrand}
              preloadImage={preloadImage}
              renderDiscoveryButton={renderDiscoveryButton}
              isDamImageV2EnabledPDP={isDamImageV2EnabledPDP}
              isDamImageV2Enabled={isDamImageV2Enabled}
              forceWebpForVideo
              imageConfigWithoutDPR={imageConfigWithoutDPR}
              fromBagPage={fromBagPage}
              isFromHeroProductTile={isFromHeroProductTile}
            />
          ) : (
            <Anchor aria-label="view full size image" noLink onClick={onOpenSimpleFullSize}>
              <DamImage
                className="full-size-desktop-image"
                data-locator={getLocator('pdp_main_image')}
                imgData={imgData}
                itemProp="contentUrl"
                isProductImage
                lazyLoad={false}
                imgConfigs={imgConfigsFunc(fromSkeleton, skeletonConfig, mainConfig)} // TODO check for imageConfig in case of having cloudinary name form for bundle
                onLoad={onLoadImage || handleImageLoaded}
                isOptimizedVideo
                dynamicBadgeOverlayQty={dynamicBadgeOverlayQty}
                badgeConfig={badgeConfig}
                tcpStyleType={tcpStyleType}
                primaryBrand={primaryBrand || alternateBrand}
                preloadImage={preloadImage}
                renderDiscoveryButton={renderDiscoveryButton}
                isDamImageV2EnabledPDP={isDamImageV2EnabledPDP}
                isDamImageV2Enabled={isDamImageV2Enabled}
                imageConfigWithoutDPR={imageConfigWithoutDPR}
                fromBagPage={fromBagPage}
                forceWebpForVideo
                isFromHeroProductTile={isFromHeroProductTile}
              />
            </Anchor>
          )}
        </BodyCopy>
      </div>
      {isImageLoaded && <RenderPerf.Measure name={HERO_VISIBLE} />}
    </>
  );
};

const getPdpImgConfig = (isDamImageV2EnabledPDP) => {
  return isDamImageV2EnabledPDP
    ? config.IMG_DATA_CLIENT_PLP_PDP_V2
    : config.IMG_DATA_CLIENT_PLP_PDP;
};

const getIsBundleZoomEnabled = ({ isFromBundlePage, isZoomEnabled }) => {
  return isFromBundlePage && isZoomEnabled;
};

const enableImageMagnifierHandlerPDP = ({ setEnableImageMagnifier, enableImageMagnifier }) => {
  if (imageMagnifierEnabler === false) {
    setEnableImageMagnifier(true);
    imageMagnifierEnabler = enableImageMagnifier || true;
    if (canUseDOM()) {
      productSectionWidth =
        document.getElementById('productDetailsSection') &&
        document.getElementById('productDetailsSection').offsetWidth;
      mainImageHeight = document
        .getElementsByClassName('redesigned-main-image-wrapper')[0]
        ?.getBoundingClientRect()?.height;
    }
  }
};

const enableImageMagnifierHandlerBundle = ({
  setEnableImageMagnifierBundle,
  enableImageMagnifierBundle,
}) => {
  if (imageMagnifierEnablerBundle === false) {
    setEnableImageMagnifierBundle(true);
    imageMagnifierEnablerBundle = enableImageMagnifierBundle || true;
    if (canUseDOM()) {
      productSectionWidthBundle =
        document.getElementsByClassName('main-bundle-description-section') &&
        document.getElementsByClassName('main-bundle-description-section')?.[0]?.offsetWidth;
      mainImageHeightBundle = document
        .getElementsByClassName('main-bundle-image')?.[0]
        ?.getBoundingClientRect()?.height;
    }
  }
};

const getMainImageHeight = (isBundleZoomEnabled) => {
  if (isBundleZoomEnabled) {
    return mainImageHeightBundle;
  }
  return mainImageHeight;
};

const getProductSectionWidth = (isBundleZoomEnabled) => {
  if (isBundleZoomEnabled) {
    return productSectionWidthBundle;
  }
  return productSectionWidth;
};

const ProductDetailImage = (props) => {
  const {
    imageName,
    imageUrl,
    zoomImageUrl,
    className,
    isZoomEnabled,
    onOpenSimpleFullSize,
    isMobile,
    imageConfig,
    fromSkeleton,
    TCPStyleQTY,
    TCPStyleType,
    isDynamicBadgeEnabledAbtest,
    fromPLPPage,
    isNewQVEnabled,
    primaryBrand,
    alternateBrand,
    isStyleWith,
    preloadImage = false,
    renderDiscoveryButton,
    isFromBundlePage,
    isPinchZoomPDPImageEnabled,
    fromQv,
    isCollectionsZoomEnabled,
    isPDP,
    imageConfigWithoutDPR,
    dynamicBadgeOverlayQty: dynamicBadgeOverlayQtyValue,
    fromBagPage,
    isFromHeroProductTile,
  } = props;
  const [isImageLoaded, handleImageLoaded] = useBooleanState(false);
  const [pdpImgLoaded, handlePdpImgLoad] = useState(false);
  const [enableImageMagnifier, setEnableImageMagnifier] = useState(false);
  const [enableImageMagnifierBundle, setEnableImageMagnifierBundle] = useState(false);
  const { extraConfig } = React.useContext(LabelsContext);
  const { isDamImageV2EnabledPDP } = extraConfig || {};
  const isBundleZoomEnabled = getIsBundleZoomEnabled({ isFromBundlePage, isZoomEnabled });

  const enableImageMagnifierHandler = () => {
    if (isZoomEnabled) {
      if (isBundleZoomEnabled) {
        enableImageMagnifierHandlerBundle({
          enableImageMagnifierBundle,
          setEnableImageMagnifierBundle,
        });
      } else {
        enableImageMagnifierHandlerPDP({ setEnableImageMagnifier, enableImageMagnifier });
      }
    }
  };

  const imgData = {
    alt: imageName,
    url: imageUrl,
  };
  const largeImgData = {
    alt: imageName,
    url: zoomImageUrl,
  };
  const style = { order: '0', width: '100%', position: 'relative' };
  const { imgConfig, badgeConfig } = getImgConfig(fromPLPPage, isDamImageV2EnabledPDP);
  const { imgConfig: pdpImgConfig } = fromPLPPage
    ? getPdpImgConfig(isDamImageV2EnabledPDP)
    : config.IMG_DATA;

  const { badgeConfig: badgeConfigMobile } = config.IMG_DATA_CLIENT;

  const dynamicBadgeOverlayQty = getDynamicBadgePackQty(
    TCPStyleType,
    TCPStyleQTY,
    isDynamicBadgeEnabledAbtest
  );

  const updatedMainImageHeight = getMainImageHeight(isBundleZoomEnabled);
  const updatedProductSectionWidth = getProductSectionWidth(isBundleZoomEnabled);
  const dynamicBadgeOverlayQtyUpdateValue = dynamicBadgeOverlayQty || dynamicBadgeOverlayQtyValue;
  return (
    <>
      <div
        itemScope
        itemType="http://schema.org/ImageObject"
        className={className}
        title={imageName}
        onMouseEnter={enableImageMagnifierHandler}
      >
        {isZoomEnabled && !fromSkeleton && (
          <>
            <ReactInputPosition
              className="input-position show-on-desktop-only"
              style={style}
              mouseActivationMethod={MOUSE_ACTIVATION.HOVER}
              trackItemPosition
              linkItemToActive
            >
              <ImageRenderer
                cursorStyle="crosshair"
                onImageLoad={handleImageLoaded}
                imgData={imgData}
                largeImgData={largeImgData}
                imgConfig={!pdpImgLoaded ? imgConfig : pdpImgConfig}
                productSectionWidth={updatedProductSectionWidth}
                dynamicBadgeOverlayQty={dynamicBadgeOverlayQtyUpdateValue}
                tcpStyleType={TCPStyleType}
                badgeConfig={badgeConfig}
                primaryBrand={primaryBrand}
                alternateBrand={alternateBrand}
                mainImageHeight={updatedMainImageHeight}
                preloadImage={preloadImage}
                renderDiscoveryButton={renderDiscoveryButton}
                isPinchZoomPDPImageEnabled={isPinchZoomPDPImageEnabled}
                bundleClassName={`${className} full-size-desktop-image`}
                isBundleZoomEnabled={isBundleZoomEnabled}
                isPDP={isPDP}
                imageConfigWithoutDPR={imageConfigWithoutDPR}
                fromBagPage={fromBagPage}
                isFromHeroProductTile={isFromHeroProductTile}
              />
            </ReactInputPosition>
            {fromPLPPage && (
              <PseudoPdpImg>
                <ReactInputPosition
                  className="psuedo-pdp-img show-on-desktop-only"
                  mouseActivationMethod={MOUSE_ACTIVATION.HOVER}
                  trackItemPosition
                  linkItemToActive
                >
                  <ImageRenderer
                    cursorStyle="crosshair"
                    onImageLoad={() => handlePdpImgLoad(true)}
                    fromPLPPage={fromPLPPage}
                    imgData={imgData}
                    largeImgData={largeImgData}
                    imgConfig={pdpImgConfig}
                    productSectionWidth={1}
                    dynamicBadgeOverlayQty={dynamicBadgeOverlayQtyUpdateValue}
                    tcpStyleType={TCPStyleType}
                    badgeConfig={badgeConfig}
                    primaryBrand={primaryBrand}
                    alternateBrand={alternateBrand}
                    mainImageHeight={0}
                    renderDiscoveryButton={renderDiscoveryButton}
                    bundleClassName={`${className} full-size-desktop-image`}
                    isBundleZoomEnabled={isBundleZoomEnabled}
                    isPDP={isPDP}
                    imageConfigWithoutDPR={imageConfigWithoutDPR}
                    fromBagPage={fromBagPage}
                    isFromHeroProductTile={isFromHeroProductTile}
                  />
                </ReactInputPosition>
              </PseudoPdpImg>
            )}
            {isImageLoaded && <RenderPerf.Measure name={HERO_VISIBLE} />}
          </>
        )}
        <NonZoomImage
          badgeConfig={badgeConfigMobile}
          tcpStyleType={TCPStyleType}
          preloadImage={preloadImage}
          {...{
            className,
            isMobile,
            imageUrl,
            imageName,
            onOpenSimpleFullSize,
            imageConfig,
            fromSkeleton,
            dynamicBadgeOverlayQty: dynamicBadgeOverlayQtyUpdateValue,
            fromPLPPage,
            isNewQVEnabled,
            primaryBrand,
            alternateBrand,
            isStyleWith,
            renderDiscoveryButton,
            isDamImageV2EnabledPDP,
            isFromBundlePage,
            fromQv,
            isCollectionsZoomEnabled,
            imageConfigWithoutDPR,
            fromBagPage,
            isFromHeroProductTile,
            isDynamicBadgeEnabledAbtest,
          }}
        />
      </div>
    </>
  );
};

ProductDetailImage.propTypes = {
  imageName: PropTypes.string,

  /** the image url */
  imageUrl: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,

  /** zoom image url */
  zoomImageUrl: PropTypes.string.isRequired,

  className: PropTypes.string,
  isZoomEnabled: PropTypes.bool,
  onOpenSimpleFullSize: PropTypes.func,
  imageConfig: PropTypes.arrayOf(PropTypes.string),
  fromSkeleton: PropTypes.bool,
  TCPStyleQTY: PropTypes.number,
  TCPStyleType: PropTypes.string,
  isDynamicBadgeEnabled: PropTypes.shape({}),
  fromPLPPage: PropTypes.bool,
  isNewQVEnabled: PropTypes.bool,
  imageConfigWithoutDPR: PropTypes.string,
  fromBagPage: PropTypes.bool,
  dynamicBadgeOverlayQty: PropTypes.number,
  isFromHeroProductTile: PropTypes.bool,
};

ProductDetailImage.defaultProps = {
  className: '',
  imageName: '',
  isZoomEnabled: true,
  onOpenSimpleFullSize: () => {},
  imageConfig: [],
  fromSkeleton: false,
  TCPStyleQTY: 0,
  TCPStyleType: '',
  isDynamicBadgeEnabled: false,
  fromPLPPage: false,
  isNewQVEnabled: false,
  imageConfigWithoutDPR: '',
  fromBagPage: false,
  dynamicBadgeOverlayQty: 0,
  isFromHeroProductTile: false,
};

const MemoizedProductDetailImage = React.memo(SafeComponent(ProductDetailImage));
export default withStyles(MemoizedProductDetailImage, styles);
export { ProductDetailImage as ProductDetailImageVanilla };
